import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  limit as fsLimit,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref, uploadBytes } from "firebase/storage";
import { db, functions, storage } from "./firebase";
import { normalizeUgandaPhone } from "./format";
import type { AppUser, Rider, RiderApplication, RiderRecord, Shipment, VehicleType } from "./types";
import { ACTIVE_STATUSES } from "./types";

function mapDocs<T>(snap: { docs: Array<{ id: string; data: () => unknown }> }): T[] {
  return snap.docs.map((d) => ({ ...(d.data() as object), id: d.id }) as T);
}

/* ---------------- users ---------------- */

export async function fetchUser(uid: string): Promise<AppUser | null> {
  const snap = await getDoc(doc(db, "users", uid));
  if (!snap.exists()) return null;
  return snap.data() as AppUser;
}

export function useUsersByRole(role: AppUser["role"]) {
  return useQuery({
    queryKey: ["users", role],
    queryFn: async () =>
      mapDocs<AppUser>(await getDocs(query(collection(db, "users"), where("role", "==", role)))),
  });
}

export function useRiders() {
  return useQuery({
    queryKey: ["riders"],
    queryFn: async () => {
      const [applicationsSnap, ridersSnap, usersSnap] = await Promise.all([
        getDocs(collection(db, "riderApplications")),
        getDocs(collection(db, "riders")),
        getDocs(query(collection(db, "users"), where("role", "==", "rider"))),
      ]);
      const users = new Map(
        usersSnap.docs.map((document) => [document.id, document.data() as AppUser]),
      );
      const profiles = new Map(
        ridersSnap.docs.map((document) => [document.id, document.data() as Rider]),
      );
      return mapDocs<RiderApplication>(applicationsSnap).map(
        (application): RiderRecord => ({
          ...application,
          profile: profiles.get(application.linkedUid),
          user: users.get(application.linkedUid),
        }),
      );
    },
  });
}

export function useShipments() {
  return useQuery({
    queryKey: ["shipments"],
    queryFn: async () => {
      const snap = await getDocs(
        query(collection(db, "shipments"), orderBy("createdAt", "desc"), fsLimit(300)),
      );
      return mapDocs<Shipment>(snap);
    },
  });
}

export function useDashboardStats() {
  const riders = useRiders();
  const shipments = useShipments();

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const todayShipments = (shipments.data ?? []).filter((s) => {
    const created = s.createdAt instanceof Timestamp ? s.createdAt.toDate() : null;
    return created ? created >= startOfToday : false;
  });

  const gmvToday = todayShipments
    .filter((s) => s.status === "delivered")
    .reduce((sum, s) => sum + s.price.customerTotalUgx, 0);
  const commissionToday = todayShipments
    .filter((s) => s.status === "delivered")
    .reduce((sum, s) => sum + s.price.platformCommissionUgx, 0);

  return {
    isLoading: riders.isLoading || shipments.isLoading,
    error: riders.error ?? shipments.error,
    totalRiders: riders.data?.length ?? 0,
    activeRidersToday: 0,
    deliveriesToday: todayShipments.length,
    gmvToday,
    commissionToday,
    recentShipments: (shipments.data ?? []).slice(0, 10),
    activeShipments: (shipments.data ?? []).filter((s) => ACTIVE_STATUSES.includes(s.status))
      .length,
  };
}

/* ---------------- mutations ---------------- */

export function useSetRiderLifecycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      uid,
      action,
    }: {
      uid: string;
      action: "approveRider" | "suspendRider" | "reactivateRider";
    }) => {
      const callable = httpsCallable<{ uid: string }, unknown>(functions, action);
      await callable({ uid });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["riders"] });
      qc.invalidateQueries({ queryKey: ["users", "customer"] });
    },
  });
}

export interface RegisterRiderInput {
  name: string;
  phone: string;
  vehicleType: VehicleType;
  plateNumber: string;
  idPhoto?: File | null;
}

export interface RegisterRiderResult {
  uid: string;
  applicationId: string;
  status: "pending_verification";
  idPhotoUploaded: boolean;
}

export function useSetCustomerStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ uid, status }: { uid: string; status: "active" | "suspended" }) => {
      await updateDoc(doc(db, "users", uid), { status, updatedAt: serverTimestamp() });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users", "customer"] }),
  });
}

interface RegisterRiderFunctionResult {
  uid: string;
  applicationId: string;
  status: "pending_verification";
}

interface RegisterRiderFunctionInput {
  displayName: string;
  phoneE164: string;
  vehicleType: VehicleType;
  plateNumber: string;
}

export function useRegisterRider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RegisterRiderInput) => {
      const phone = normalizeUgandaPhone(input.phone);
      const createRider = httpsCallable<
        RegisterRiderFunctionInput,
        RegisterRiderFunctionResult
      >(functions, "registerRider");
      const response = await createRider({
        displayName: input.name.trim(),
        phoneE164: phone,
        vehicleType: input.vehicleType,
        plateNumber: input.plateNumber.trim(),
      });

      let idPhotoUploaded = false;
      if (input.idPhoto) {
        try {
          const safeName = input.idPhoto.name.replace(/[^a-zA-Z0-9._-]/g, "-");
          const storageRef = ref(
            storage,
            `rider-applications/${response.data.applicationId}/documents/${Date.now()}-${safeName}`,
          );
          await uploadBytes(storageRef, input.idPhoto, { contentType: input.idPhoto.type });
          const attachDocument = httpsCallable<
            { uid: string; documentType: "nationalId"; storagePath: string },
            unknown
          >(functions, "attachRiderDocument");
          await attachDocument({
            uid: response.data.uid,
            documentType: "nationalId",
            storagePath: storageRef.fullPath,
          });
          idPhotoUploaded = true;
        } catch {
          // Preserve the application so its document can be attached before approval.
        }
      }

      return { ...response.data, idPhotoUploaded } satisfies RegisterRiderResult;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["riders"] }),
  });
}
